/**
 * siteDocumentPersistence — what a `SiteDocument` looks like when it is
 * embedded in the Firestore site record (`users/{uid}/sites/{siteId}.documents[]`).
 *
 * Why: the site record is one Firestore document with a hard 1 MiB limit,
 * and the store used to write every uploaded document into it verbatim —
 * `textContent` + `fullExtractedText` + `pages[].text`, three copies of the
 * same body. Measured 2026-09-07: Redhead Park at 852 KB with 471 KB of
 * document text across 32 documents. The full body already has a home
 * (the per-document GCS JSON and the chunk subcollection), so the record
 * only needs the metadata and the prompt preview.
 *
 * Contract:
 *   - `fullExtractedText` and `topicSegments` are never persisted on the site
 *     record (the compact `topics` union is).
 *   - Source-backed rows keep a 1k metadata preview; legacy inline-only rows
 *     retain the existing 10k preview. The SED has a separate 10k projection.
 *   - `pages[]` keep their offsets (page numbers and char ranges are what
 *     lineage gates read); text is reconstructed from the preview or hydrated body.
 *   - In-memory state is untouched: the store keeps full bodies for the
 *     session; only the Firestore payload is projected.
 */
import type { SiteDocument } from '../types/siteRag';
import { EXTRACTED_TEXT_MAX_CHARS, SITE_DOCUMENT_PREVIEW_MAX_CHARS } from './docExtractionLimits';

type SitePage = NonNullable<SiteDocument['pages']>[number];

function pageForRecord(page: SitePage): SitePage {
  return page.text.length === 0 ? page : { ...page, text: '' };
}

/** A source locator and captured-length receipt are required before reducing
 * the existing preview. This proves eligibility, not that storage is readable:
 * evidence readers still validate/hydrate the body before using it. */
export function hasSourceBackedDocumentBody(doc: SiteDocument): boolean {
  if (!/^[A-Za-z0-9_-]+$/.test(doc.id) || !/^[a-f0-9]{64}$/i.test(doc.contentHash ?? '')) return false;
  const path = /^users\/([A-Za-z0-9_-]+)\/documents\/([A-Za-z0-9_-]+)\.json$/.exec(doc.storagePath ?? '');
  return path?.[2] === doc.id && Number.isSafeInteger(doc.extractedCharsKept)
    && (doc.extractedCharsKept ?? 0) > 0
    && (doc.extractedCharsKept ?? 0) >= (doc.textContent?.length ?? 0);
}

/** Project one document into its site-record shape. Returns the same object when nothing changes. */
export function documentForSiteRecord(doc: SiteDocument): SiteDocument {
  const previewChars = hasSourceBackedDocumentBody(doc)
    ? SITE_DOCUMENT_PREVIEW_MAX_CHARS : EXTRACTED_TEXT_MAX_CHARS;
  const needsTextCap = typeof doc.textContent === 'string' && doc.textContent.length > previewChars;
  const hasFullBody = doc.fullExtractedText !== undefined || doc.topicSegments !== undefined || doc.visualReadings !== undefined;
  const pages = doc.pages;
  const pagesNeedWork = Array.isArray(pages) && pages.some((p) => p.text.length > 0);
  if (!needsTextCap && !hasFullBody && !pagesNeedWork) return doc;

  const { fullExtractedText: _full, topicSegments: _segments, visualReadings: _visual, ...rest } = doc;
  const projected: SiteDocument = rest as SiteDocument;
  if (_visual?.length) projected.visualReadingCount = _visual.length;
  if (needsTextCap) projected.textContent = doc.textContent.slice(0, previewChars);
  if (pagesNeedWork && pages) projected.pages = pages.map((p) => pageForRecord(p));
  return projected;
}

/** Project a documents array for the site record. */
export function documentsForSiteRecord(docs: readonly SiteDocument[]): SiteDocument[] {
  return docs.map(documentForSiteRecord);
}

/**
 * Apply the projection to a site patch that may carry `documents`. Any other
 * key passes through untouched, so this is safe as the single chokepoint for
 * generic `updateSite` / `createSite` writes.
 */
export function stripDocumentBodiesFromSitePatch<T extends object>(patch: T): T {
  const docs = (patch as { documents?: unknown }).documents;
  if (!Array.isArray(docs)) return patch;
  return { ...patch, documents: documentsForSiteRecord(docs as SiteDocument[]) };
}

/** Bytes a documents array would occupy in the record (UTF-8 JSON). Used by the backfill report. */
export function documentsRecordBytes(docs: readonly SiteDocument[]): number {
  const json = JSON.stringify(docs);
  return typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(json).length : json.length;
}
